import React from 'react';
import { LayoutDashboard, Map, TrendingDown, Droplets, AlertTriangle, GitBranch, Menu, X, Globe, FileText, LogOut, Brain, History } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

const getMenuItems = (t) => [
  { id: 'overview', icon: LayoutDashboard, label: t('nav.overview') },
  { id: 'geographic', icon: Globe, label: t('nav.geographic') },
  { id: 'network', icon: Map, label: t('nav.network') },
  { id: 'flow', icon: GitBranch, label: t('nav.flow') },
  { id: 'nrw', icon: TrendingDown, label: t('nav.nrw') },
  { id: 'quality', icon: Droplets, label: t('nav.quality') },
  { id: 'alerts', icon: AlertTriangle, label: t('nav.alerts') },
  { id: 'smart-analytics', icon: Brain, label: t('nav.smartAnalytics') },
  { id: 'historical', icon: History, label: t('nav.historical') || 'Historical Data' },
  { id: 'reports', icon: FileText, label: t('nav.reports') },
];

export const Sidebar = ({ activeSection, setActiveSection, sidebarOpen, setSidebarOpen }) => {
  const { logout, user } = useAuth();
  const { t } = useLanguage();
  const menuItems = getMenuItems(t);
  
  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };
  
  return (
    <>
      {/* Sidebar for desktop */}
      <aside 
        className={cn(
          "hidden md:flex flex-col bg-card border-r border-border transition-all duration-300",
          sidebarOpen ? "w-64" : "w-16"
        )}
      >
        {/* Logo/Header */}
        <div className="p-4 lg:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={cn(
              "rounded-lg bg-primary flex items-center justify-center flex-shrink-0 transition-all",
              sidebarOpen ? "w-10 h-10" : "w-8 h-8"
            )}>
              <Droplets className={cn("text-primary-foreground", sidebarOpen ? "w-6 h-6" : "w-5 h-5")} />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-lg font-bold text-foreground leading-tight">SWATI</h1>
                <p className="text-[10px] text-muted-foreground leading-tight">Smart Water Audit with</p>
                <p className="text-[10px] text-muted-foreground leading-tight">Telemetry and IOT</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-2 lg:p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={activeSection === item.id ? "default" : "ghost"}
                className={cn(
                  "w-full gap-3 transition-all",
                  sidebarOpen ? "justify-start px-3" : "justify-center px-0",
                  activeSection === item.id && "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
                onClick={() => setActiveSection(item.id)}
                title={!sidebarOpen ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </Button>
            );
          })}
        </nav>
        
        {/* Footer */}
        <div className="p-2 lg:p-4 border-t border-border space-y-2">
          {/* User info */}
          {sidebarOpen && user && (
            <div className="px-2 py-2 mb-2 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">{t('auth.loggedInAs')}</p>
              <p className="text-sm font-medium text-foreground truncate">{user.username}</p>
            </div>
          )}
          
          {/* Logout button */}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full gap-2 text-red-600 hover:text-red-700 hover:bg-red-50",
              !sidebarOpen && "justify-center px-0"
            )}
            onClick={handleLogout}
            title={!sidebarOpen ? t('auth.logout') : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>{t('auth.logout')}</span>}
          </Button>
          
          {/* Collapse toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>
      </aside>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Mobile sidebar */}
      <aside 
        className={cn(
          "md:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-card border-r border-border z-50 transform transition-transform duration-300 flex flex-col",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo/Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Droplets className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground leading-tight">SWATI</h1>
                <p className="text-[10px] text-muted-foreground leading-tight">Smart Water Audit</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={activeSection === item.id ? "default" : "ghost"}
                className={cn(
                  "w-full justify-start gap-3",
                  activeSection === item.id && "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
                onClick={() => {
                  setActiveSection(item.id);
                  setSidebarOpen(false);
                }}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Button>
            );
          })}
        </nav>
        
        {/* Mobile footer */}
        <div className="p-3 border-t border-border">
          {user && (
            <div className="px-2 py-2 mb-2 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">{t('auth.loggedInAs')}</p>
              <p className="text-sm font-medium text-foreground">{user.username}</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            <span>{t('auth.logout')}</span>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;